// Reading `/api/connectors/`, once.
//
// The connector list serves the screens that need the ERP connector ITSELF —
// its id for a Link-to-ERP dialog, its presence for an informational banner.
// Whether a screen may offer a write the ERP would refuse is a different read
// (hooks/useWriteGate.js). A misread list still misleads, so an unreadable
// payload is "unknown", never "no connectors".
//
// The endpoint has exactly one shape: `ConnectorViewSet` overrides no
// `pagination_class`, so it inherits `StandardPagination` and answers
// `{count, next, previous, results}`, pinned by
// backend/exact_app/tests/test_connector_endpoints.py. Any other body is
// unreadable, never "no connectors".
//
// Null contract rather than a throw: the reader reports "unreadable" as a
// value and the hook (hooks/useConnectors.js) decides how to surface it.

/**
 * The store's connector rows, or `null` if this is not the WHOLE list.
 *
 * The `count` check: nothing pages this endpoint, so an ERP connector sitting
 * on page 2 and a store with no ERP at all produce the
 * same first page.
 *
 * @param {object|null} payload  parsed DRF page
 * @returns {Array|null}
 */
export function connectorRowsFrom(payload) {
  const rows = payload?.results;
  if (!Array.isArray(rows)) return null;
  if (typeof payload.count !== 'number' || payload.count !== rows.length) return null;
  // An empty list IS an answer here, unlike the ISO reference tables: a
  // Manexys-managed store legitimately has no connectors.
  return rows;
}

/**
 * The ERP connector that currently owns this store's PO lifecycle and
 * supplier master, or `null` when none does.
 *
 * PENDING and DISCONNECTED do not count — the ERP takes the PO lifecycle
 * only while the connector is ACTIVE.
 *
 * @param {Array} rows  from `connectorRowsFrom` (what `useConnectors` serves)
 * @returns {object|null}  the connector row, for screens that name or link it
 */
export function activeErpConnector(rows) {
  return rows.find((c) => c.role === 'ERP' && c.status === 'ACTIVE') ?? null;
}
